import { Box, StyleProps } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";

import { useThrottledValue } from "../hooks/useThrottle";
import { useTheme } from "./ThemedChakraProvider";

type AudioIndicatorProps = Omit<StyleProps, "pos" | "position"> & {
  /**
   * Should be in the `[0, 100]` range
   */
  volume: number;
  parentBg: StyleProps["bg"];
  numBars?: number;
  /** A percentage of the total width (`1` = `1%`) */
  spacing?: number;
};

export const AudioIndicator: React.FC<AudioIndicatorProps> = ({
  volume: volumeProp,
  parentBg,
  numBars = 5,
  spacing = 10,
  ...styles
}) => {
  const { colors } = useTheme();

  const indicatorRef = useRef<HTMLDivElement>(null);
  const volume = useThrottledValue(
    Math.round(Math.min(100, Math.max(0, volumeProp))),
    100
  );
  useEffect(() => {
    indicatorRef.current?.style.setProperty("width", `${volume}%`);
  }, [volume]);

  const numSpacers = numBars - 1;
  const spacerWidth = spacing; // percentage, not pixels
  const barWidth = (100 - numSpacers * spacerWidth) / numBars;

  return (
    <Box bg="gray.200" {...styles} pos="relative">
      <Box
        ref={indicatorRef}
        transition="width 50ms"
        bg={`linear-gradient(to right, ${colors.green[500]} 90%, transparent)`}
        pos="absolute"
        h="100%"
        left="0"
      />
      {[...Array(numSpacers)].map((_, idx) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          bg={parentBg}
          pos="absolute"
          height="100%"
          width={`${spacerWidth}%`}
          left={`${idx * (barWidth + spacerWidth) + barWidth}%`}
        />
      ))}
    </Box>
  );
};
